import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import Button, { TYPES } from 'components/Button';
import Paragraph from 'components/Paragraph';
import VariantModel from 'models/Variant';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';
import { StoreLocationUtils } from 'utils/StoreLocation';

import { INVENTORY_LEVELS, INVENTORY_LABELS } from './constants';

const SelectedStoreInfo = ({ openModal, variant, preferredStore, callToReserveClicked }) => {
  const sizeIsSelected = variant?.id;
  const storeIsSelected = preferredStore?.handle;
  const inventoryIsAvailable = StoreLocationUtils.isInventoryAvailableOnSize(variant);

  return (
    <div className="SelectedStoreInfo">
      {sizeIsSelected && !inventoryIsAvailable && (
        <div>
          <Paragraph>
            {I18n.localize('pdp.storeInventory.dropDown.inventoryNotAvailable')}{' '}
          </Paragraph>
        </div>
      )}
      {!sizeIsSelected && (
        <div>
          <Paragraph>{I18n.localize('pdp.storeInventory.dropDown.selectSize')}</Paragraph>
        </div>
      )}
      {!storeIsSelected && sizeIsSelected && inventoryIsAvailable && (
        <div>
          <Paragraph>
            {I18n.localize('pdp.storeInventory.dropDown.checkStoreNearYou')}{' '}
            <Button
              onClick={() => openModal('Find A Store')}
              type={TYPES.LINK}
              className="PdpStoreInventory__recommendation-link"
            >
              {I18n.localize('pdp.storeInventory.dropDown.findAStore')}
            </Button>
          </Paragraph>
        </div>
      )}
      {storeIsSelected && sizeIsSelected && inventoryIsAvailable && (
        <div
          className={classnames('SelectedStoreInfo__details-container', {
            'SelectedStoreInfo__details--no-inventory':
              preferredStore?.inventoryLevel === INVENTORY_LEVELS.NO_INVENTORY,
            'SelectedStoreInfo__details--low-inventory':
              preferredStore?.inventoryLevel === INVENTORY_LEVELS.LOW_INVENTORY,
            'SelectedStoreInfo__details--high-inventory':
              preferredStore?.inventoryLevel === INVENTORY_LEVELS.HIGH_INVENTORY,
          })}
        >
          <Paragraph>
            {I18n.localize('pdp.storeInventory.dropDown.callToReserveSubheader')}{' '}
            <Button
              onClick={() => openModal('See All Stores')}
              type={TYPES.LINK}
              className="PdpStoreInventory__recommendation-link"
            >
              {I18n.localize('pdp.storeInventory.dropDown.seeAllStores')}
            </Button>
          </Paragraph>
          <div className="SelectedStoreInfo__option__store">
            <Paragraph className="SelectedStoreInfo__option__store-inventory">
              {INVENTORY_LABELS[preferredStore?.inventoryLevel]}
            </Paragraph>
            <Paragraph className="SelectedStoreInfo__option__store-name">
              {preferredStore.storeName}
            </Paragraph>
            <Paragraph>{preferredStore.streetAddress}</Paragraph>
            <Paragraph>{preferredStore.phoneNumber}</Paragraph>
            <Button
              className="SelectedStoreInfo__option__store-phone-reserve"
              type={TYPES.OUTLINE}
              linkTo={`tel:${preferredStore.phoneNumber.replace(/[^a-zA-Z0-9]/g, '')}`}
              onClick={() => callToReserveClicked(preferredStore.handle)}
            >
              {I18n.localize('pdp.storeInventory.dropDown.callToReserveButton')}
            </Button>
          </div>
        </div>
      )}
      <style jsx>
        {`
          .SelectedStoreInfo {
            position: relative;
            display: flex;
            background-color: ${Theme.get('colors-white')};
          }

          .SelectedStoreInfo__option__store {
            background-color: ${Theme.get('colors-black-10')};
            padding: ${Theme.get('spacing') * 2.5}px;
          }
        `}
      </style>
      <style jsx global>
        {`
          .SelectedStoreInfo__option .Paragraph {
            margin-bottom: ${Theme.get('spacing') / 2}px;
          }

          .Paragraph.SelectedStoreInfo__option__store-name {
            font-weight: 700;
          }

          .SelectedStoreInfo__option__store .Paragraph {
            font-size: 16px;
            margin-bottom: ${Theme.get('spacing') / 2}px;
          }

          .PdpStoreInventory__recommendation-link.Button {
            letter-spacing: 0.5px;
          }

          .SelectedStoreInfo__option__store-phone-reserve.Button {
            margin-top: 16px;
          }

          .Paragraph.SelectedStoreInfo__option__store-inventory {
            font-weight: 700;
          }

          .SelectedStoreInfo__details--no-inventory
            .Paragraph.SelectedStoreInfo__option__store-inventory {
            color: ${Theme.get('colors-discount-grey')};
          }

          .SelectedStoreInfo__details--low-inventory
            .Paragraph.SelectedStoreInfo__option__store-inventory {
            color: ${Theme.get('colors-discount-yellow')};
          }

          .SelectedStoreInfo__details--high-inventory
            .Paragraph.SelectedStoreInfo__option__store-inventory {
            color: ${Theme.get('colors-success')};
          }
        `}
      </style>
    </div>
  );
};

SelectedStoreInfo.propTypes = {
  openModal: PropTypes.func.isRequired,
  preferredStore: PropTypes.shape({
    handle: PropTypes.string,
    streetAddress: PropTypes.string,
    storeName: PropTypes.string,
    phoneNumber: PropTypes.string,
    inventoryLevel: PropTypes.string,
    handle: PropTypes.string,
  }),
  variant: PropTypes.instanceOf(VariantModel),
  callToReserveClicked: PropTypes.func.isRequired,
};

SelectedStoreInfo.defaultProps = {
  preferredStore: {
    handle: null,
  },
};

export default SelectedStoreInfo;
